import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import DiscussionCard from '../components/DiscussionCard';
import { useAuth } from '../context/AuthContext';
import { discussionService } from '../services/api';
import { Discussion } from '../types';

type DiscussionStatus = 'draft' | 'in_review' | 'ready' | 'published' | 'archived';

const LIFECYCLE_STATUSES: DiscussionStatus[] = ['draft', 'in_review', 'ready', 'published'];

const STATUS_LABELS: Record<DiscussionStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  ready: 'Ready',
  published: 'Published',
  archived: 'Archived',
};

function getTransitionTargets(status: DiscussionStatus, permissions: string[]): DiscussionStatus[] {
  const baseMap: Record<DiscussionStatus, DiscussionStatus[]> = {
    draft: ['in_review', 'published'],
    in_review: ['draft', 'ready'],
    ready: ['in_review', 'published'],
    published: ['archived'],
    archived: ['published'],
  };

  return (baseMap[status] || []).filter((target) => {
    if (target === 'published') {
      return status === 'archived'
        ? permissions.includes('manage_any_discussion')
        : permissions.includes('publish_discussions');
    }
    return permissions.includes('review_discussions');
  });
}

const StaffDraftQueuePage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const canAccess = !!user?.permissions?.includes('view_unpublished_discussions');
  const [items, setItems] = useState<Discussion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transitioningId, setTransitioningId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [query, setQuery] = useState('');
  const [hazardFilter, setHazardFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<'all' | DiscussionStatus>('all');

  useEffect(() => {
    if (!canAccess) {
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all(LIFECYCLE_STATUSES.map((status) => discussionService.getAll(status, 100, 0)))
      .then((responses) => {
        const merged = responses.flatMap((response) => response.data as Discussion[])
          .sort((left, right) => new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime());
        setItems(merged);
      })
      .catch(() => setError('Failed to load draft queue'))
      .finally(() => setLoading(false));
  }, [canAccess]);

  const hazardOptions = useMemo(() => {
    const options = new Set<string>();
    items.forEach((discussion) => {
      (discussion.hazard_tags || []).forEach((hazard) => options.add(hazard));
    });
    return ['all', ...Array.from(options).sort()];
  }, [items]);

  const statusCounts = useMemo(() => {
    const counts: Record<DiscussionStatus, number> = {
      draft: 0,
      in_review: 0,
      ready: 0,
      published: 0,
      archived: 0,
    };

    items.forEach((discussion) => {
      counts[discussion.status as DiscussionStatus] += 1;
    });

    return counts;
  }, [items]);

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return items.filter((discussion) => {
      const matchesHazard = hazardFilter === 'all' || (discussion.hazard_tags || []).includes(hazardFilter);
      const matchesStatus = statusFilter === 'all' || discussion.status === statusFilter;
      if (!matchesHazard) {
        return false;
      }

      if (!matchesStatus) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const haystack = `${discussion.title} ${discussion.summary || discussion.discussion_body || ''} ${discussion.tags.join(' ')} ${(discussion.hazard_tags || []).join(' ')}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [items, hazardFilter, query, statusFilter]);

  const transitionDiscussion = async (discussionId: string, nextStatus: DiscussionStatus) => {
    setTransitioningId(discussionId);
    setError(null);

    try {
      const response = await discussionService.transition(discussionId, nextStatus, notes[discussionId]?.trim() || undefined);
      setNotes((current) => ({ ...current, [discussionId]: '' }));
      const updated = response.data as Discussion;
      setItems((current) => current.map((discussion) => (discussion.id === updated.id ? updated : discussion)));
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || 'Failed to transition lifecycle status');
    } finally {
      setTransitioningId(null);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccess) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="page-shell">
      <div className="container">
        <div className="page-intro">
          <div>
            <p className="eyebrow">Staff operations</p>
            <h1 className="page-heading">Lifecycle queue</h1>
            <p className="page-copy">
              Track products through lifecycle stages, filter by hazard or status, and move discussions forward as event operations evolve.
            </p>
          </div>
          <div className="hero-actions">
            <Link to="/compose" className="btn btn-primary">New draft</Link>
            <Link to="/staff/ops-center" className="btn btn-secondary">Ops center</Link>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="dashboard-grid archive-summary-grid">
          <div className="module-card">
            <p className="eyebrow">Drafts</p>
            <h3 className="metric-value">{statusCounts.draft}</h3>
            <p className="module-note">Awaiting review submission</p>
          </div>
          <div className="module-card">
            <p className="eyebrow">In review / Ready</p>
            <h3 className="metric-value">{statusCounts.in_review + statusCounts.ready}</h3>
            <p className="module-note">Products in editorial pipeline</p>
          </div>
          <div className="module-card">
            <p className="eyebrow">Filtered</p>
            <h3 className="metric-value">{filteredItems.length}</h3>
            <p className="module-note">Queue entries matching current filters</p>
          </div>
        </div>

        <div className="card filter-card">
          <div className="form-grid two-column">
            <div className="form-group">
              <label className="form-label" htmlFor="draft-query">Search queue</label>
              <input
                id="draft-query"
                className="form-input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search title, content, tags, or hazards"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="draft-hazard">Hazard focus</label>
              <select
                id="draft-hazard"
                className="form-input"
                value={hazardFilter}
                onChange={(event) => setHazardFilter(event.target.value)}
              >
                {hazardOptions.map((option) => (
                  <option key={option} value={option}>{option === 'all' ? 'All hazards' : option}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="status-filter">Lifecycle status</label>
            <select
              id="status-filter"
              className="form-input"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as 'all' | DiscussionStatus)}
            >
              <option value="all">All statuses</option>
              {LIFECYCLE_STATUSES.map((status) => (
                <option key={status} value={status}>{STATUS_LABELS[status]}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="card">Loading draft queue...</div>
        ) : filteredItems.length === 0 ? (
          <div className="card">No drafts match the current queue filters.</div>
        ) : (
          <div className="discussion-stack">
            {filteredItems.map((discussion) => {
              const transitionTargets = getTransitionTargets(discussion.status as DiscussionStatus, user?.permissions || []);

              return (
                <article key={discussion.id}>
                  <DiscussionCard discussion={discussion} />
                  <div className="card" style={{ marginTop: '-0.75rem' }}>
                    <div className="discussion-card-topline">
                      <span className="eyebrow">Lifecycle</span>
                      <span className="chip chip-static">{STATUS_LABELS[discussion.status as DiscussionStatus]}</span>
                    </div>
                    {transitionTargets.length > 0 && (
                      <div className="form-group">
                        <label className="form-label" htmlFor={`note-${discussion.id}`}>Note for the author (optional)</label>
                        <input
                          id={`note-${discussion.id}`}
                          className="form-input"
                          maxLength={2000}
                          value={notes[discussion.id] || ''}
                          onChange={(event) => setNotes((current) => ({ ...current, [discussion.id]: event.target.value }))}
                          placeholder="Saved to the history; sent with returns to the review channel"
                        />
                      </div>
                    )}
                    <div className="hero-actions">
                      {transitionTargets.length === 0 ? (
                        <span className="module-note">No transition actions available for your role.</span>
                      ) : (
                        transitionTargets.map((target) => (
                          <button
                            key={`${discussion.id}-${target}`}
                            type="button"
                            className="btn btn-secondary"
                            disabled={transitioningId === discussion.id}
                            onClick={() => transitionDiscussion(discussion.id, target)}
                          >
                            {transitioningId === discussion.id ? 'Updating...' : `Move to ${STATUS_LABELS[target]}`}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffDraftQueuePage;